import { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { Badge, Collapse, Table } from "reactstrap";
import { Button } from "../../Styled";
import { ModalBox, ModalOverlay } from "../../Modals/ModalStyled";
import {
  collectObsoleteBomLines,
  getObsoleteLineKey,
  groupSubstitutesBySection,
  OBSOLETE_ALT_SELECTED_ROW_CLASS,
} from "../../../constants/digiKeyColumns";

const formatPrice = (alternate) => {
  if (alternate.unit_price_display) {
    return alternate.unit_price_display;
  }
  if (alternate.unit_price != null) {
    return `$${Number(alternate.unit_price).toFixed(5)}`;
  }
  return "N/A";
};

const substitutesFromLine = (line) =>
  Array.isArray(line?.substitutes) ? line.substitutes : [];

const ObsoleteAlternateTable = ({
  substitutes,
  selectedMpn,
  onSelect,
  radioName,
}) => {
  const sections = groupSubstitutesBySection(substitutes);

  if (!sections.length) {
    return <p className="text-muted mb-0">No DigiKey alternates were found.</p>;
  }

  return sections.map((section) => (
    <div key={section.key} className="mb-3">
      <h6 className="mb-2">{section.title}</h6>
      <Table className="table-bordered table-sm mb-0">
        <thead>
          <tr>
            <th />
            <th>Type</th>
            <th>MPN</th>
            <th>Manufacturer</th>
            <th>DigiKey PN</th>
            <th>Unit Price</th>
            <th>Package</th>
          </tr>
        </thead>
        <tbody>
          {section.items.map((alternate, index) => {
            const mpn = String(alternate.manufacturer_part_number || "").trim();
            const isSelected = selectedMpn === mpn;

            return (
              <tr
                key={`${mpn}-${index}`}
                className={classnames({
                  [OBSOLETE_ALT_SELECTED_ROW_CLASS]: isSelected,
                })}
                onClick={() => onSelect(mpn)}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <input
                    type="radio"
                    name={radioName}
                    checked={isSelected}
                    onChange={() => onSelect(mpn)}
                  />
                </td>
                <td>{alternate.substitute_type || "N/A"}</td>
                <td>{mpn}</td>
                <td>{alternate.manufacturer || "N/A"}</td>
                <td>{alternate.digikey_part_number || "N/A"}</td>
                <td>{formatPrice(alternate)}</td>
                <td>{alternate.package || "N/A"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  ));
};

/**
 * Uses substitutes already attached to bom lines from digikey_enrichment.
 * Does not call DigiKey / Oxylabs again when opening the modal.
 */
const ObsoletePartsReviewModal = ({
  show,
  bomlines,
  focusLineKey,
  onSave,
  onClose,
}) => {
  const obsoleteLines = useMemo(
    () => collectObsoleteBomLines(bomlines || []),
    [bomlines]
  );

  const [expandedKey, setExpandedKey] = useState("");
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }

    const initialSelections = {};

    obsoleteLines.forEach((line) => {
      const key = getObsoleteLineKey(line);
      if (line.selected_alternate_mpn) {
        initialSelections[key] = String(line.selected_alternate_mpn).trim();
      }
    });

    setSelections(initialSelections);
    setExpandedKey(
      focusLineKey ||
        (obsoleteLines[0] ? getObsoleteLineKey(obsoleteLines[0]) : "")
    );
  }, [show, obsoleteLines, focusLineKey]);

  const toggleAccordion = (line) => {
    const key = getObsoleteLineKey(line);
    setExpandedKey((prev) => (prev === key ? "" : key));
  };

  if (!show || !obsoleteLines.length) {
    return null;
  }

  const handleSave = async () => {
    const payload = obsoleteLines
      .map((line) => {
        const key = getObsoleteLineKey(line);
        const selectedMpn = selections[key];
        if (!selectedMpn) {
          return null;
        }
        const alternate = substitutesFromLine(line).find(
          (item) =>
            String(item.manufacturer_part_number || "").trim() === selectedMpn
        );
        if (!alternate) {
          return null;
        }
        return { line, alternate };
      })
      .filter(Boolean);

    if (!payload.length) {
      return;
    }

    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = Object.values(selections).filter(Boolean).length;

  return (
    <ModalOverlay className={classnames({ show })}>
      <ModalBox
        className="theme-form"
        style={{ maxWidth: "960px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="obsolete-parts-modal-header">
          <h5 className="mb-0">Obsolete Parts – Select Alternates</h5>
          <button
            type="button"
            className="obsolete-parts-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <p className="text-muted mb-3">
          {obsoleteLines.length} obsolete part(s) found. Expand each item to
          review substitutes and alternates, then save your selections.
        </p>

        <div className="obsolete-parts-accordion mb-3">
          {obsoleteLines.map((line) => {
            const key = getObsoleteLineKey(line);
            const obsoleteMpn =
              line.obsolete_part_number || line.manufacturer_part_number;
            const substitutes = substitutesFromLine(line);
            const alternateCount =
              substitutes.length ||
              line.alternate_count ||
              0;
            const isOpen = expandedKey === key;
            const selectedMpn = selections[key] || "";

            return (
              <div key={key} className="obsolete-accordion-item mb-2">
                <button
                  type="button"
                  className={classnames(
                    "obsolete-accordion-header w-100 d-flex align-items-center justify-content-between",
                    { open: isOpen }
                  )}
                  onClick={() => toggleAccordion(line)}
                >
                  <span>
                    <strong>{obsoleteMpn}</strong>
                    {line.manufacturer ? (
                      <span className="text-muted ml-2">{line.manufacturer}</span>
                    ) : null}
                  </span>
                  <span className="d-flex align-items-center">
                    {selectedMpn ? (
                      <Badge color="success" className="mr-2">
                        Selected: {selectedMpn}
                      </Badge>
                    ) : null}
                    <Badge color="warning">{alternateCount}</Badge>
                  </span>
                </button>
                <Collapse isOpen={isOpen}>
                  <div className="obsolete-accordion-body p-3">
                    <ObsoleteAlternateTable
                      substitutes={substitutes}
                      selectedMpn={selectedMpn}
                      radioName={`obsolete-alt-${key}`}
                      onSelect={(mpn) =>
                        setSelections((prev) => ({ ...prev, [key]: mpn }))
                      }
                    />
                  </div>
                </Collapse>
              </div>
            );
          })}
        </div>

        <div className="obsolete-parts-modal-footer d-flex justify-content-between align-items-center">
          <span className="text-muted small">
            {selectedCount} of {obsoleteLines.length} part(s) selected
          </span>
          <div className="obsolete-parts-modal-actions d-flex align-items-center">
            <Button
              type="button"
              className="importBtn blue px-4 w-auto mr-2"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="importBtn blue px-4 w-auto"
              disabled={saving || selectedCount === 0}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save Selections"}
            </Button>
          </div>
        </div>
      </ModalBox>
    </ModalOverlay>
  );
};

export default ObsoletePartsReviewModal;
